import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Card, Grid, Stack, Typography, Button, MenuItem, TextField, Alert, Chip, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, ToggleButton, ToggleButtonGroup,
  IconButton, Tooltip, Skeleton, alpha, useTheme, useMediaQuery,
} from '@mui/material';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as ChartTooltip, Legend, Cell,
} from 'recharts';
import {
  Users, Briefcase, ShoppingBag, MessageSquare, Send, IndianRupee, BadgeIndianRupee, FileText,
  RefreshCw, Printer, CalendarRange, TrendingUp, TrendingDown,
} from 'lucide-react';
import axios from '../utils/axios';
import PageHeader from '../components/common/PageHeader';
import { AppDateField } from '../components/common/AppDateField';
import ExportTableButton from '../components/common/ExportTableButton';
import KpiTile from '../components/charts/KpiTile';
import DonutCard from '../components/charts/DonutCard';

/* ─────────────────────────── formatting ─────────────────────────── */

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const int = (n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
const fmt = (v, format) => (format === 'inr' ? inr(v) : format === '%' ? `${int(v)}%` : int(v));
const changeText = (c) => (c === null || c === undefined ? 'new' : `${c > 0 ? '+' : ''}${c}%`);

const PRETTY = {
  category_enquiry: 'Category enquiry', buyer_requirement: 'Buyer requirement', product_page: 'Product page',
  business_page: 'Storefront page', rfq: 'RFQ', chat: 'Chat', admin: 'Admin', unknown: 'Unknown',
  new: 'New', contacted: 'Contacted', negotiating: 'Negotiating', won: 'Won', lost: 'Lost',
  open: 'Open', in_progress: 'In progress', closed: 'Closed', expired: 'Expired',
  buyer: 'Buyer', reseller: 'Reseller',
};
const pretty = (s) => PRETTY[String(s || '').trim().toLowerCase()] || s || '(not set)';

/* ─────────────────────────── periods ─────────────────────────── */

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const quarterStart = (d, shift = 0) => new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3 + shift * 3, 1);

// Presets resolve to concrete dates; "qtd" is sent with no params so the
// server's own IST default (current quarter to date vs previous quarter) wins.
const PRESETS = [
  { key: 'qtd', label: 'This quarter vs last quarter' },
  { key: 'lastq', label: 'Last quarter vs the one before' },
  { key: 'mtd', label: 'This month vs last month' },
  { key: 'd90', label: 'Last 90 days vs previous 90' },
  { key: 'fy', label: 'This financial year vs last' },
  { key: 'custom', label: 'Custom dates' },
];
const presetRange = (key, now = new Date()) => {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  switch (key) {
    case 'qtd': {
      const s = quarterStart(today);
      return { from: ymd(s), to: ymd(today), cmpFrom: ymd(quarterStart(today, -1)), cmpTo: ymd(addDays(s, -1)) };
    }
    case 'lastq': {
      const s = quarterStart(today, -1);
      const e = addDays(quarterStart(today), -1);
      return { from: ymd(s), to: ymd(e), cmpFrom: ymd(quarterStart(today, -2)), cmpTo: ymd(addDays(s, -1)) };
    }
    case 'mtd': {
      const s = new Date(today.getFullYear(), today.getMonth(), 1);
      const ps = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      return { from: ymd(s), to: ymd(today), cmpFrom: ymd(ps), cmpTo: ymd(addDays(s, -1)) };
    }
    case 'd90': {
      const s = addDays(today, -89);
      return { from: ymd(s), to: ymd(today), cmpFrom: ymd(addDays(s, -90)), cmpTo: ymd(addDays(s, -1)) };
    }
    case 'fy': {
      const fyStartYear = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
      const s = new Date(fyStartYear, 3, 1);
      const ps = new Date(fyStartYear - 1, 3, 1);
      return { from: ymd(s), to: ymd(today), cmpFrom: ymd(ps), cmpTo: ymd(addDays(s, -1)) };
    }
    default:
      return null;
  }
};

/* ─────────────────────────── small building blocks ─────────────────────────── */

const Section = ({ title, subtitle, action, children }) => (
  <Box component="section" sx={{ mt: { xs: 3, md: 4 } }}>
    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'flex-end' }} spacing={1} mb={1.5}>
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>{title}</Typography>
        {subtitle && <Typography variant="body2" color="text.secondary">{subtitle}</Typography>}
      </Box>
      {action}
    </Stack>
    {children}
  </Box>
);

const Panel = ({ title, subtitle, children, sx }) => (
  <Card sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 3, height: '100%', width: '100%', maxWidth: '100%', ...sx }}>
    {title && (
      <Box mb={1.5}>
        <Typography sx={{ fontWeight: 800, fontSize: 15 }}>{title}</Typography>
        {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
      </Box>
    )}
    {children}
  </Card>
);

const Delta = ({ change, lowerIsBetter = false }) => {
  const theme = useTheme();
  if (change === 0) return <Typography component="span" sx={{ fontSize: 12, color: 'text.secondary', fontWeight: 700 }}>0%</Typography>;
  const good = change === null ? !lowerIsBetter : (change > 0) !== lowerIsBetter;
  const color = good ? theme.palette.success.main : theme.palette.error.main;
  const Icon = change === null || change > 0 ? TrendingUp : TrendingDown;
  return (
    <Stack component="span" direction="row" alignItems="center" spacing={0.25} sx={{ color, fontSize: 12, fontWeight: 800, display: 'inline-flex' }}>
      <Icon size={13} /><span>{changeText(change)}</span>
    </Stack>
  );
};

const ChartTip = ({ active, payload, label, format }) => {
  if (!active || !payload?.length) return null;
  return (
    <Box sx={{ bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 2, p: 1.25, boxShadow: 3, minWidth: 140 }}>
      <Typography sx={{ fontSize: 12, fontWeight: 800, mb: 0.5 }}>{label}</Typography>
      {payload.map((p) => (
        <Stack key={p.dataKey} direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: p.color }} />
            <Typography sx={{ fontSize: 12 }}>{p.name}</Typography>
          </Stack>
          <Typography sx={{ fontSize: 12, fontWeight: 800 }}>{fmt(p.value, format)}</Typography>
        </Stack>
      ))}
    </Box>
  );
};

/** Two-period comparison table for a breakdown list [{label, cur, prev, change}]. */
const CompareTable = ({ rows = [], curLabel, prevLabel, format, empty = 'No data in either period.', maxRows = 10 }) => (
  <TableContainer sx={{ overflowX: 'auto' }}>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Name</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700, color: 'text.secondary', whiteSpace: 'nowrap' }}>{curLabel}</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700, color: 'text.secondary', whiteSpace: 'nowrap' }}>{prevLabel}</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700, color: 'text.secondary' }}>Change</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.length === 0 ? (
          <TableRow><TableCell colSpan={4}><Typography variant="body2" color="text.secondary">{empty}</Typography></TableCell></TableRow>
        ) : rows.slice(0, maxRows).map((r) => (
          <TableRow key={r.label} hover>
            <TableCell sx={{ fontWeight: 600, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={pretty(r.label)}>{pretty(r.label)}</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(r.cur, format)}</TableCell>
            <TableCell align="right" sx={{ color: 'text.secondary' }}>{fmt(r.prev, format)}</TableCell>
            <TableCell align="right"><Delta change={r.change} /></TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

/** Grouped bar chart of a breakdown, current vs compare. */
const CompareBars = ({ rows = [], height = 260, curName, prevName, format, colors }) => {
  const data = rows.slice(0, 8).map((r) => ({ name: pretty(r.label), cur: r.cur, prev: r.prev }));
  if (!data.length) return <Typography variant="body2" color="text.secondary">No data in either period.</Typography>;
  return (
    <Box sx={{ width: '100%', height }}>
      <ResponsiveContainer>
        <BarChart data={data} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={colors.grid} />
          <XAxis dataKey="name" tick={{ fontSize: 11, fill: colors.axis }} interval={0} tickLine={false} axisLine={false}
            tickFormatter={(v) => (String(v).length > 12 ? `${String(v).slice(0, 11)}…` : v)} />
          <YAxis tick={{ fontSize: 11, fill: colors.axis }} tickLine={false} axisLine={false}
            tickFormatter={(v) => (format === 'inr' ? `₹${v >= 1000 ? `${Math.round(v / 1000)}k` : v}` : v)} />
          <ChartTooltip content={<ChartTip format={format} />} cursor={{ fill: colors.cursor }} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar dataKey="cur" name={curName} fill={colors.cur} radius={[4, 4, 0, 0]} maxBarSize={28} />
          <Bar dataKey="prev" name={prevName} fill={colors.prev} radius={[4, 4, 0, 0]} maxBarSize={28} />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

const TREND_METRICS = [
  { key: 'signups', label: 'Users' },
  { key: 'businesses', label: 'Businesses' },
  { key: 'products', label: 'Products' },
  { key: 'enquiries', label: 'Enquiries' },
  { key: 'leads', label: 'Leads' },
  { key: 'revenue', label: 'Revenue', format: 'inr' },
];

/* ─────────────────────────── page ─────────────────────────── */

export default function WbrReportView() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const colors = useMemo(() => ({
    cur: theme.palette.primary.main,
    prev: theme.palette.mode === 'dark' ? alpha(theme.palette.grey[400], 0.7) : theme.palette.grey[400],
    grid: alpha(theme.palette.grey[500], 0.2),
    axis: theme.palette.text.secondary,
    cursor: alpha(theme.palette.grey[500], 0.08),
  }), [theme]);

  const [preset, setPreset] = useState('qtd');
  const [range, setRange] = useState(() => presetRange('qtd'));
  const [applied, setApplied] = useState({ preset: 'qtd', range: presetRange('qtd') });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [trendKey, setTrendKey] = useState('signups');

  const load = useCallback(async (fresh = false) => {
    setLoading(true);
    setError('');
    try {
      const params = applied.preset === 'qtd' ? {} : { ...applied.range };
      if (fresh) params.fresh = 1;
      const res = await axios.get('/admin/wbr', { params, skipNotify: 'success' });
      setData(res.data);
      // Reflect the server-resolved dates back into the inputs.
      if (applied.preset === 'qtd' && res.data?.period) {
        setRange({ from: res.data.period.from, to: res.data.period.to, cmpFrom: res.data.compare.from, cmpTo: res.data.compare.to });
      }
    } catch (e) {
      setError(e?.response?.data?.msg || 'Could not load the report.');
    } finally {
      setLoading(false);
    }
  }, [applied]);

  useEffect(() => { load(); }, [load]);

  const onPreset = (key) => {
    setPreset(key);
    const r = presetRange(key);
    if (r) {
      setRange(r);
      setApplied({ preset: key, range: r });
    }
  };
  const onDate = (field) => (e) => { setPreset('custom'); setRange((r) => ({ ...r, [field]: e.target.value })); };
  const rangeValid = range?.from && range?.to && range.from <= range.to && (!range.cmpFrom || !range.cmpTo || range.cmpFrom <= range.cmpTo);
  const applyCustom = () => setApplied({ preset: 'custom', range: { ...range } });

  const m = useMemo(() => Object.fromEntries((data?.metrics || []).map((x) => [x.key, x])), [data]);
  const curName = isMobile ? 'Current' : 'Current period';
  const prevName = isMobile ? 'Compare' : 'Compare period';

  const trendData = useMemo(() => {
    if (!data) return [];
    const cur = data.trends.cur[trendKey] || [];
    const prev = data.trends.prev[trendKey] || [];
    return Array.from({ length: Math.max(cur.length, prev.length) }, (_, i) => ({
      name: `W${i + 1}`, cur: cur[i] ?? null, prev: prev[i] ?? null,
    }));
  }, [data, trendKey]);
  const trendFormat = TREND_METRICS.find((t) => t.key === trendKey)?.format;

  const funnelRows = useMemo(() => {
    if (!data) return [];
    const steps = [['sellers', 'Signed up as seller'], ['withBusiness', 'Created a business'], ['withProduct', 'Listed a product'], ['paid', 'Bought a paid plan']];
    const c0 = data.funnel.cur.sellers || 0;
    const p0 = data.funnel.prev.sellers || 0;
    return steps.map(([k, label]) => ({
      label, cur: data.funnel.cur[k] || 0, prev: data.funnel.prev[k] || 0,
      curPct: c0 ? Math.round(((data.funnel.cur[k] || 0) / c0) * 100) : 0,
      prevPct: p0 ? Math.round(((data.funnel.prev[k] || 0) / p0) * 100) : 0,
    }));
  }, [data]);

  const revenueMix = useMemo(() => (data ? [
    { name: curName, Subscriptions: m.subRevenue?.cur || 0, 'Lead top-ups': m.topupRevenue?.cur || 0, Promotions: m.promoRevenue?.cur || 0 },
    { name: prevName, Subscriptions: m.subRevenue?.prev || 0, 'Lead top-ups': m.topupRevenue?.prev || 0, Promotions: m.promoRevenue?.prev || 0 },
  ] : []), [data, m, curName, prevName]);

  const monthlyData = useMemo(() => {
    if (!data) return [];
    const label = (ym) => new Date(`${ym}-01T00:00:00`).toLocaleString('en-IN', { month: 'short', year: '2-digit' });
    return [
      ...data.monthly.prev.map((x) => ({ ...x, name: label(x.month), period: 'prev' })),
      ...data.monthly.cur.map((x) => ({ ...x, name: label(x.month), period: 'cur' })),
    ];
  }, [data]);

  const [catSort, setCatSort] = useState('activity');
  const categoryRows = useMemo(() => {
    const list = [...(data?.categories || [])];
    return list.sort((a, b) => (b[catSort] || 0) - (a[catSort] || 0));
  }, [data, catSort]);
  const deadCategories = (data?.categories || []).filter((c) => c.known && !c.inactive && c.activity === 0 && c.liveProducts === 0);

  const metricGroups = useMemo(() => {
    const groups = new Map();
    (data?.metrics || []).forEach((x) => { if (!groups.has(x.group)) groups.set(x.group, []); groups.get(x.group).push(x); });
    return [...groups.entries()];
  }, [data]);

  const kpis = [
    { k: 'signups', label: 'New users', icon: <Users size={20} />, color: '#FE5F01', trend: 'signups' },
    { k: 'businesses', label: 'New businesses', icon: <Briefcase size={20} />, color: '#3B82F6', trend: 'businesses' },
    { k: 'productsNew', label: 'New products', icon: <ShoppingBag size={20} />, color: '#10B981', trend: 'products' },
    { k: 'enquiries', label: 'Buyer enquiries', icon: <MessageSquare size={20} />, color: '#8B5CF6', trend: 'enquiries' },
    { k: 'leads', label: 'Leads delivered', icon: <Send size={20} />, color: '#06B6D4', trend: 'leads' },
    { k: 'revenue', label: 'Total revenue', icon: <IndianRupee size={20} />, color: '#059669', trend: 'revenue' },
    { k: 'payingUsers', label: 'Paying sellers', icon: <BadgeIndianRupee size={20} />, color: '#D97706' },
    { k: 'rfqs', label: 'RFQs posted', icon: <FileText size={20} />, color: '#EC4899' },
  ];

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'hidden', pb: 6, '@media print': { '& .wbr-no-print': { display: 'none !important' } } }}>
      <PageHeader
        title="WBR Report"
        subtitle={data ? `${data.period.label}  vs  ${data.compare.label}` : 'Company business review'}
        action={(
          <Stack direction="row" spacing={1} className="wbr-no-print">
            <Tooltip title="Recalculate now (skips the 5-minute cache)">
              <span>
                <IconButton aria-label="Refresh report" onClick={() => load(true)} disabled={loading}><RefreshCw size={18} /></IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Print / save as PDF">
              <IconButton aria-label="Print report" onClick={() => window.print()}><Printer size={18} /></IconButton>
            </Tooltip>
            <ExportTableButton
              data={data?.metrics || []}
              total={data?.metrics?.length || 0}
              columns={[
                { label: 'Area', key: 'group' },
                { label: 'Metric', key: 'label' },
                { label: `Current (${data?.period?.from || ''} to ${data?.period?.to || ''})`, key: 'cur' },
                { label: `Compare (${data?.compare?.from || ''} to ${data?.compare?.to || ''})`, key: 'prev' },
                { label: 'Change %', value: (r) => (r.change === null ? 'new' : r.change) },
              ]}
              filename={`wbr_${data?.period?.from || 'report'}_${data?.period?.to || ''}`}
            />
          </Stack>
        )}
      />

      {/* ── Filters ── */}
      <Card className="wbr-no-print" sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 3, mt: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              select fullWidth size="small" label="Period" value={preset}
              onChange={(e) => onPreset(e.target.value)}
              InputProps={{ startAdornment: <CalendarRange size={16} style={{ marginRight: 8, opacity: 0.6 }} /> }}
            >
              {PRESETS.map((p) => <MenuItem key={p.key} value={p.key}>{p.label}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}><AppDateField fullWidth size="small" label="From" value={range?.from || ''} onChange={onDate('from')} /></Grid>
          <Grid size={{ xs: 6, md: 2 }}><AppDateField fullWidth size="small" label="To" value={range?.to || ''} onChange={onDate('to')} /></Grid>
          <Grid size={{ xs: 6, md: 2 }}><AppDateField fullWidth size="small" label="Compare from" value={range?.cmpFrom || ''} onChange={onDate('cmpFrom')} /></Grid>
          <Grid size={{ xs: 6, md: 2 }}><AppDateField fullWidth size="small" label="Compare to" value={range?.cmpTo || ''} onChange={onDate('cmpTo')} /></Grid>
          <Grid size={{ xs: 12, md: 1 }}>
            <Button fullWidth variant="contained" disabled={preset !== 'custom' || !rangeValid || loading} onClick={applyCustom} sx={{ fontWeight: 700, height: 40 }}>
              Apply
            </Button>
          </Grid>
        </Grid>
        {preset === 'custom' && !rangeValid && (
          <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 1 }}>
            "From" must be on or before "To". Leave the compare dates empty to compare with the same length of time just before.
          </Typography>
        )}
      </Card>

      {error && <Alert severity="error" sx={{ mt: 2 }} action={<Button color="inherit" size="small" onClick={() => load(true)}>Retry</Button>}>{error}</Alert>}

      {loading && !data ? (
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {Array.from({ length: 8 }).map((_, i) => (
            <Grid size={{ xs: 12, sm: 6, lg: 3 }} key={i}><Skeleton variant="rounded" height={140} sx={{ borderRadius: 3 }} /></Grid>
          ))}
          <Grid size={{ xs: 12 }}><Skeleton variant="rounded" height={320} sx={{ borderRadius: 3 }} /></Grid>
        </Grid>
      ) : data && (
        <Box sx={{ opacity: loading ? 0.6 : 1, transition: 'opacity .2s' }}>
          {/* ── Headline ── */}
          <Section title="Headline" subtitle="Current period, with change against the compare period. Sparklines show the week-by-week trend.">
            <Grid container spacing={2}>
              {kpis.map((k) => {
                const x = m[k.k] || {};
                const series = k.trend ? (data.trends.cur[k.trend] || []).map((v) => ({ v })) : [{ v: x.prev || 0 }, { v: x.cur || 0 }];
                return (
                  <Grid size={{ xs: 12, sm: 6, lg: 3 }} key={k.k}>
                    <KpiTile
                      label={k.label}
                      value={fmt(x.cur, x.format)}
                      icon={k.icon}
                      color={k.color}
                      growth={typeof x.change === 'number' ? x.change : undefined}
                      secondary={`vs ${fmt(x.prev, x.format)}${x.change === null ? ' · new' : ''}`}
                      series={series}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </Section>

          {/* ── Trend ── */}
          <Section
            title="Week-by-week trend"
            subtitle="Week 1 is the first 7 days of each period, so both periods line up."
            action={(
              <ToggleButtonGroup size="small" exclusive value={trendKey} onChange={(_, v) => v && setTrendKey(v)} className="wbr-no-print"
                sx={{ flexWrap: 'wrap', '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 700, px: 1.25 } }}>
                {TREND_METRICS.map((t) => <ToggleButton key={t.key} value={t.key}>{t.label}</ToggleButton>)}
              </ToggleButtonGroup>
            )}
          >
            <Panel>
              <Box sx={{ width: '100%', height: { xs: 240, md: 320 } }}>
                <ResponsiveContainer>
                  <LineChart data={trendData} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={colors.grid} />
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: colors.axis }} tickLine={false} axisLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: colors.axis }} tickLine={false} axisLine={false}
                      tickFormatter={(v) => (trendFormat === 'inr' ? `₹${v >= 1000 ? `${Math.round(v / 1000)}k` : v}` : v)} />
                    <ChartTooltip content={<ChartTip format={trendFormat} />} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Line type="monotone" dataKey="cur" name={curName} stroke={colors.cur} strokeWidth={2.5} dot={{ r: 3 }} connectNulls />
                    <Line type="monotone" dataKey="prev" name={prevName} stroke={colors.prev} strokeWidth={2} strokeDasharray="5 4" dot={false} connectNulls />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </Panel>
          </Section>

          {/* ── Users ── */}
          <Section title="Users & onboarding" subtitle="Who joined, and how far new sellers got.">
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Panel title="New users by type">
                  <CompareBars rows={data.breakdowns.signupsByType} curName={curName} prevName={prevName} colors={colors} />
                </Panel>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Panel title="Seller onboarding funnel" subtitle="Sellers who signed up in each period, and the share that reached each step (as of today).">
                  <Stack spacing={1.75} sx={{ mt: 1 }}>
                    {funnelRows.map((f) => (
                      <Box key={f.label}>
                        <Stack direction="row" justifyContent="space-between" mb={0.5}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>{f.label}</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {int(f.cur)} <Typography component="span" variant="caption" color="text.secondary">({f.curPct}%) · was {int(f.prev)} ({f.prevPct}%)</Typography>
                          </Typography>
                        </Stack>
                        <Box sx={{ position: 'relative', height: 10, borderRadius: 5, bgcolor: alpha(colors.prev, 0.25), overflow: 'hidden' }}>
                          <Box sx={{ position: 'absolute', inset: 0, width: `${f.prevPct}%`, bgcolor: alpha(colors.prev, 0.6) }} />
                          <Box sx={{ position: 'absolute', top: 2, bottom: 2, left: 0, width: `${f.curPct}%`, bgcolor: colors.cur, borderRadius: 5 }} />
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                </Panel>
              </Grid>
            </Grid>
          </Section>

          {/* ── Catalogue ── */}
          <Section
            title="Catalogue by category"
            subtitle="New activity in the current period (compare period in grey). Live products and sub-categories are today's totals."
            action={(
              <TextField select size="small" label="Sort by" value={catSort} onChange={(e) => setCatSort(e.target.value)} sx={{ minWidth: 170 }} className="wbr-no-print">
                <MenuItem value="activity">Most activity</MenuItem>
                <MenuItem value="liveProducts">Live products</MenuItem>
                <MenuItem value="products">New products</MenuItem>
                <MenuItem value="businesses">New businesses</MenuItem>
                <MenuItem value="enquiries">Enquiries</MenuItem>
                <MenuItem value="leads">Leads</MenuItem>
              </TextField>
            )}
          >
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, lg: 8 }}>
                <Panel>
                  <TableContainer sx={{ overflowX: 'auto', maxHeight: 460 }}>
                    <Table size="small" stickyHeader sx={{ minWidth: 640 }}>
                      <TableHead>
                        <TableRow>
                          {['Category', 'Live products', 'Sub-cats', 'New products', 'New businesses', 'Enquiries', 'Leads'].map((h, i) => (
                            <TableCell key={h} align={i ? 'right' : 'left'} sx={{ fontWeight: 700, color: 'text.secondary', whiteSpace: 'nowrap' }}>{h}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {categoryRows.map((c) => (
                          <TableRow key={c.category} hover sx={c.activity === 0 ? { opacity: 0.55 } : undefined}>
                            <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                              {c.category}
                              {!c.known && <Chip size="small" label="not a category" color="warning" variant="outlined" sx={{ ml: 1, height: 18, fontSize: 10 }} />}
                              {c.inactive && <Chip size="small" label="inactive" variant="outlined" sx={{ ml: 1, height: 18, fontSize: 10 }} />}
                            </TableCell>
                            <TableCell align="right">{int(c.liveProducts)}</TableCell>
                            <TableCell align="right">{int(c.subCategories)}</TableCell>
                            {[['products', 'productsPrev'], ['businesses', 'businessesPrev'], ['enquiries', 'enquiriesPrev'], ['leads', 'leadsPrev']].map(([a, b]) => (
                              <TableCell key={a} align="right" sx={{ whiteSpace: 'nowrap' }}>
                                <Typography component="span" sx={{ fontWeight: 700, fontSize: 13 }}>{int(c[a])}</Typography>
                                <Typography component="span" sx={{ color: 'text.disabled', fontSize: 12 }}> / {int(c[b])}</Typography>
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Panel>
              </Grid>
              <Grid size={{ xs: 12, lg: 4 }}>
                <Stack spacing={2} sx={{ height: '100%' }}>
                  <DonutCard
                    title="New products by category"
                    subtitle="Current period"
                    data={categoryRows.filter((c) => c.products > 0).sort((a, b) => b.products - a.products).slice(0, 8).map((c) => ({ name: c.category, count: c.products }))}
                    height={220}
                  />
                  {deadCategories.length > 0 && (
                    <Alert severity="warning" variant="outlined">
                      <Typography sx={{ fontWeight: 700, fontSize: 13 }}>{deadCategories.length} active categor{deadCategories.length === 1 ? 'y has' : 'ies have'} no products and no activity</Typography>
                      <Typography variant="caption">{deadCategories.slice(0, 8).map((c) => c.category).join(', ')}{deadCategories.length > 8 ? '…' : ''}</Typography>
                    </Alert>
                  )}
                </Stack>
              </Grid>
            </Grid>
          </Section>

          {/* ── Demand ── */}
          <Section title="Demand & leads" subtitle="Buyer enquiries fan out into leads (one lead = one seller receiving an enquiry).">
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 4 }}>
                <DonutCard title="Leads by channel" subtitle="Current period" data={data.breakdowns.leadsBySource.filter((r) => r.cur > 0).map((r) => ({ name: pretty(r.label), count: r.cur }))} height={220} />
              </Grid>
              <Grid size={{ xs: 12, md: 8 }}>
                <Panel title="Lead outcomes" subtitle={`Win rate ${int(m.leadWinRate?.cur)}% (was ${int(m.leadWinRate?.prev)}%) · ${int(m.leadsPerEnquiry?.cur)} leads per enquiry`}>
                  <CompareBars rows={data.breakdowns.leadsByStatus} curName={curName} prevName={prevName} colors={colors} height={240} />
                </Panel>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Panel title="Enquiries by type"><CompareTable rows={data.breakdowns.enquiriesByType} curLabel={curName} prevLabel={prevName} /></Panel>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Panel title="RFQs by status" subtitle={`${int(m.rfqQuotes?.cur)} quotes sent (was ${int(m.rfqQuotes?.prev)})`}>
                  <CompareTable rows={data.breakdowns.rfqByStatus} curLabel={curName} prevLabel={prevName} />
                </Panel>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Panel title="Top site searches"><CompareTable rows={data.breakdowns.searches} curLabel={curName} prevLabel={prevName} maxRows={10} /></Panel>
              </Grid>
            </Grid>
          </Section>

          {/* ── Revenue ── */}
          <Section title="Revenue" subtitle="Razorpay-paid subscriptions (amount actually charged) + paid lead top-ups + promotions. Plans bought with referral wallet are shown separately, not as revenue.">
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Panel title="Revenue mix">
                  <Box sx={{ width: '100%', height: 260 }}>
                    <ResponsiveContainer>
                      <BarChart data={revenueMix} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={colors.grid} />
                        <XAxis type="number" tick={{ fontSize: 11, fill: colors.axis }} tickLine={false} axisLine={false} tickFormatter={(v) => `₹${v >= 1000 ? `${Math.round(v / 1000)}k` : v}`} />
                        <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: colors.axis }} tickLine={false} axisLine={false} width={isMobile ? 64 : 110} />
                        <ChartTooltip content={<ChartTip format="inr" />} cursor={{ fill: colors.cursor }} />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        <Bar dataKey="Subscriptions" stackId="r" fill={colors.cur} maxBarSize={34} />
                        <Bar dataKey="Lead top-ups" stackId="r" fill="#3B82F6" maxBarSize={34} />
                        <Bar dataKey="Promotions" stackId="r" fill="#10B981" radius={[0, 4, 4, 0]} maxBarSize={34} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                  <Divider sx={{ my: 1.5 }} />
                  <Stack direction="row" flexWrap="wrap" gap={2}>
                    {['revenue', 'paidOrders', 'arppu', 'walletPlanValue'].map((k) => (
                      <Box key={k} sx={{ minWidth: 120 }}>
                        <Typography variant="caption" color="text.secondary">{m[k]?.label}</Typography>
                        <Typography sx={{ fontWeight: 800 }}>{fmt(m[k]?.cur, m[k]?.format)} <Delta change={m[k]?.change} /></Typography>
                      </Box>
                    ))}
                  </Stack>
                </Panel>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Panel title="Subscription revenue by plan">
                  <CompareTable rows={data.breakdowns.revenueByPlan} curLabel={curName} prevLabel={prevName} format="inr" />
                </Panel>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Panel title="Month by month" subtitle="Compare-period months first (grey), then current-period months.">
                  <Box sx={{ width: '100%', height: 260 }}>
                    <ResponsiveContainer>
                      <BarChart data={monthlyData} margin={{ top: 8, right: 8, left: -4, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={colors.grid} />
                        <XAxis dataKey="name" tick={{ fontSize: 11, fill: colors.axis }} tickLine={false} axisLine={false} />
                        <YAxis yAxisId="r" tick={{ fontSize: 11, fill: colors.axis }} tickLine={false} axisLine={false} tickFormatter={(v) => `₹${v >= 1000 ? `${Math.round(v / 1000)}k` : v}`} />
                        <YAxis yAxisId="n" orientation="right" tick={{ fontSize: 11, fill: colors.axis }} tickLine={false} axisLine={false} />
                        <ChartTooltip cursor={{ fill: colors.cursor }} formatter={(v, name) => (name === 'Revenue' ? inr(v) : int(v))} />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        <Bar yAxisId="r" dataKey="revenue" name="Revenue" fill={colors.cur} radius={[4, 4, 0, 0]} maxBarSize={30}>
                          {monthlyData.map((d) => <Cell key={`${d.period}-${d.month}`} fill={d.period === 'prev' ? colors.prev : colors.cur} />)}
                        </Bar>
                        <Bar yAxisId="n" dataKey="signups" name="New users" fill="#3B82F6" radius={[4, 4, 0, 0]} maxBarSize={18} />
                        <Bar yAxisId="n" dataKey="leads" name="Leads" fill="#06B6D4" radius={[4, 4, 0, 0]} maxBarSize={18} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </Panel>
              </Grid>
            </Grid>
          </Section>

          {/* ── Geography ── */}
          <Section title="Geography" subtitle="Top cities and states in the current period.">
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6, lg: 3 }}><Panel title="New businesses by city"><CompareTable rows={data.geo.bizCity} curLabel="Cur" prevLabel="Prev" /></Panel></Grid>
              <Grid size={{ xs: 12, md: 6, lg: 3 }}><Panel title="New products by city"><CompareTable rows={data.geo.prodCity} curLabel="Cur" prevLabel="Prev" /></Panel></Grid>
              <Grid size={{ xs: 12, md: 6, lg: 3 }}><Panel title="Enquiries by city"><CompareTable rows={data.geo.enqCity} curLabel="Cur" prevLabel="Prev" /></Panel></Grid>
              <Grid size={{ xs: 12, md: 6, lg: 3 }}><Panel title="New businesses by state"><CompareTable rows={data.geo.bizState} curLabel="Cur" prevLabel="Prev" /></Panel></Grid>
            </Grid>
          </Section>

          {/* ── Company snapshot ── */}
          <Section title="Company today" subtitle="Running totals as of now, not limited to the selected period.">
            <Grid container spacing={2}>
              {[
                ['Total users', data.snapshot.users], ['Businesses', data.snapshot.businesses],
                ['Live products', data.snapshot.productsActive], ['All products', data.snapshot.productsAll],
                ['Active categories', data.snapshot.categories], ['Active sub-categories', data.snapshot.subCategories],
                ['Brands', data.snapshot.brands], ['Active paid subscriptions', data.snapshot.activeSubs],
                ['Approved partners', data.snapshot.partners],
              ].map(([label, v]) => (
                <Grid size={{ xs: 6, sm: 4, md: 3, lg: 2 }} key={label}>
                  <Panel sx={{ p: 2 }}>
                    <Typography variant="caption" color="text.secondary">{label}</Typography>
                    <Typography sx={{ fontWeight: 800, fontSize: 20 }}>{int(v)}</Typography>
                  </Panel>
                </Grid>
              ))}
              <Grid size={{ xs: 12, sm: 8, md: 6, lg: 4 }}>
                <Panel sx={{ p: 2 }}>
                  <Typography variant="caption" color="text.secondary">Users by type</Typography>
                  <Stack direction="row" flexWrap="wrap" gap={0.75} mt={0.75}>
                    {data.snapshot.usersByType.map((u) => <Chip key={u.label} size="small" label={`${u.label}: ${int(u.value)}`} />)}
                  </Stack>
                </Panel>
              </Grid>
            </Grid>
          </Section>

          {/* ── All metrics ── */}
          <Section title="All metrics" subtitle="Every number in this report. Red/green respects direction — e.g. fewer unmatched enquiries is green.">
            <Grid container spacing={2}>
              {metricGroups.map(([group, rows]) => (
                <Grid size={{ xs: 12, md: 6, xl: 4 }} key={group}>
                  <Panel title={group}>
                    <Table size="small">
                      <TableBody>
                        {rows.map((r) => (
                          <TableRow key={r.key} hover>
                            <TableCell sx={{ pl: 0 }}>{r.label}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 800, whiteSpace: 'nowrap' }}>{fmt(r.cur, r.format)}</TableCell>
                            <TableCell align="right" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>{fmt(r.prev, r.format)}</TableCell>
                            <TableCell align="right" sx={{ pr: 0, whiteSpace: 'nowrap' }}><Delta change={r.change} lowerIsBetter={r.lowerIsBetter} /></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Panel>
                </Grid>
              ))}
            </Grid>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
              Generated {new Date(data.generated_at).toLocaleString('en-IN')}. Figures are cached for 5 minutes; use refresh to recalculate.
            </Typography>
          </Section>
        </Box>
      )}
    </Box>
  );
}
